import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { toggleFavorite, getFavoriteListingIds } from '../../ducks/user.duck';

import css from './FavoriteButton.module.css';

const HeartIcon = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} aria-hidden="true">
    <path
      d="M12 20.5s-7.5-4.7-7.5-10A4.2 4.2 0 0 1 12 7.5a4.2 4.2 0 0 1 7.5 3c0 5.3-7.5 10-7.5 10Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Heart button to save/unsave a listing to the user's favorites.
 * Safe to place inside a link (ListingCard) - clicks don't navigate.
 * Hidden on the user's own listings (you don't save your own pieces).
 *
 * @param {Object} props
 * @param {string} props.listingId listing UUID as a string
 * @param {string} [props.authorId] listing author UUID - hides the button for own listings
 * @param {string} [props.className]
 */
const FavoriteButton = props => {
  const { listingId, authorId, className } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();
  const currentUser = useSelector(state => state.user.currentUser);

  const isOwnListing = !!authorId && currentUser?.id?.uuid === authorId;
  if (isOwnListing) {
    return null;
  }

  const isSaved = getFavoriteListingIds(currentUser).includes(listingId);

  const handleClick = e => {
    // Inside a NamedLink card - don't navigate to the listing.
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser?.id) {
      // Not logged in: send to login.
      history.push(pathByRouteName('LoginPage', routeConfiguration));
      return;
    }
    // Optimistisk toggle i user.duck - hjertet skifter med det samme,
    // serveren opdateres i kø i baggrunden. Ingen klik-spærre nødvendig.
    dispatch(toggleFavorite(listingId)).catch(() => null);
  };

  const label = isSaved
    ? intl.formatMessage({ id: 'FavoriteButton.remove' })
    : intl.formatMessage({ id: 'FavoriteButton.save' });

  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames(css.root, className, { [css.saved]: isSaved })}
      aria-label={label}
      aria-pressed={isSaved}
      title={label}
    >
      <HeartIcon filled={isSaved} />
    </button>
  );
};

export default FavoriteButton;
